/**
 * Input Sanitization Utility
 * Prevents XSS attacks by sanitizing user input before storing in Firestore
 */

/**
 * Sanitize text input - removes HTML tags and dangerous characters
 * @param {string} input - Input string to sanitize
 * @param {object} options - Sanitization options
 * @returns {string} Sanitized string
 */
export const sanitizeInput = (input, options = {}) => {
  if (typeof input !== 'string') {
    return String(input || '').trim();
  }

  const {
    maxLength = 500,
    allowNewlines = false,
  } = options;

  // Remove HTML tags
  let sanitized = input.replace(/<[^>]*>/g, '');

  // Remove script tags and event handlers
  sanitized = sanitized
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

  // Remove dangerous characters
  sanitized = sanitized
    .replace(/[<>]/g, '') // Remove angle brackets
    .replace(/&/g, '&amp;') // Escape ampersands
    .replace(/"/g, '&quot;') // Escape quotes
    .replace(/'/g, '&#x27;'); // Escape apostrophes

  // Handle newlines
  if (!allowNewlines) {
    sanitized = sanitized.replace(/\n/g, ' ').replace(/\r/g, ' ');
  }

  // Trim and limit length
  sanitized = sanitized.trim().substring(0, maxLength);

  return sanitized;
};

/**
 * Sanitize team name - alphanumeric, spaces, hyphens, underscores only
 * @param {string} input - Team name to sanitize
 * @returns {string} Sanitized team name
 */
export const sanitizeTeamName = (input) => {
  if (typeof input !== 'string') return '';
  
  // Allow alphanumeric, spaces, hyphens, underscores, and common team name characters
  const sanitized = input
    .trim()
    .replace(/[^a-zA-Z0-9\s\-_&]/g, '') // Remove special chars except allowed ones
    .replace(/\s+/g, ' ') // Normalize whitespace
    .substring(0, 100); // ✅ Max 100 characters (DoS prevention)
  
  return sanitized;
};

/**
 * Sanitize player name/IGN - alphanumeric, spaces, hyphens, underscores only
 * @param {string} input - Player name to sanitize
 * @returns {string} Sanitized player name
 */
export const sanitizePlayerName = (input) => {
  if (typeof input !== 'string') return '';
  
  // Allow alphanumeric, spaces, hyphens, underscores
  const sanitized = input
    .trim()
    .replace(/[^a-zA-Z0-9\s\-_]/g, '') // Remove special chars
    .replace(/\s+/g, ' ') // Normalize whitespace
    .substring(0, 50); // ✅ Max 50 characters (DoS prevention)
  
  return sanitized;
};

/**
 * Sanitize match type - uppercase alphanumeric, spaces, hyphens only
 * @param {string} input - Match type to sanitize
 * @returns {string} Sanitized match type
 */
export const sanitizeMatchType = (input) => {
  if (typeof input !== 'string') return '';
  
  // Allow alphanumeric, spaces, hyphens
  const sanitized = input
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9\s-]/g, '') // Remove special chars
    .replace(/\s+/g, ' ') // Normalize whitespace
    .substring(0, 50); // ✅ Max 50 characters (DoS prevention)
  
  return sanitized;
};

/**
 * Sanitize region name - uppercase alphanumeric, spaces only
 * @param {string} input - Region name to sanitize
 * @returns {string} Sanitized region name
 */
export const sanitizeRegion = (input) => {
  if (typeof input !== 'string') return '';
  
  // Allow alphanumeric, spaces
  const sanitized = input
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '') // Remove special chars
    .replace(/\s+/g, ' ') // Normalize whitespace
    .substring(0, 50); // ✅ Max 50 characters (DoS prevention)
  
  return sanitized;
};

/**
 * Sanitize object recursively
 * @param {object} obj - Object to sanitize
 * @param {object} fieldMap - Map of field names to sanitization functions
 * @returns {object} Sanitized object
 */
export const sanitizeObject = (obj, fieldMap = {}) => {
  if (!obj || typeof obj !== 'object') return obj;

  const sanitized = { ...obj };

  Object.keys(sanitized).forEach((key) => {
    const value = sanitized[key];
    
    // Use custom sanitizer if provided
    if (fieldMap[key] && typeof fieldMap[key] === 'function') {
      sanitized[key] = fieldMap[key](value);
    } else if (typeof value === 'string') {
      // Default sanitization for strings
      sanitized[key] = sanitizeInput(value);
    } else if (Array.isArray(value)) {
      // Recursively sanitize arrays
      sanitized[key] = value.map(item => 
        typeof item === 'string' ? sanitizeInput(item) : item
      );
    } else if (typeof value === 'object' && value !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeObject(value, fieldMap);
    }
  });

  return sanitized;
};

/**
 * Validate and sanitize form data before submission
 * @param {object} formData - Form data to sanitize
 * @param {object} schema - Schema defining which fields need which sanitization
 * @returns {object} Sanitized form data
 */
export const sanitizeFormData = (formData, schema = {}) => {
  const sanitized = { ...formData };
  
  // Default sanitizers for common fields
  const defaultSanitizers = {
    teamName: sanitizeTeamName,
    playerName: sanitizePlayerName,
    ign: sanitizePlayerName,
    matchType: sanitizeMatchType,
    region: sanitizeRegion,
    enemyTeam: sanitizeTeamName,
    enemyRegion: sanitizeRegion,
    title: sanitizeInput,
    name: sanitizeInput,
    note: (input) => sanitizeInput(input, { allowNewlines: true, maxLength: 1000 }),
  };

  const sanitizers = { ...defaultSanitizers, ...schema };

  Object.keys(sanitized).forEach((key) => {
    const value = sanitized[key];
    if (sanitizers[key] && typeof sanitizers[key] === 'function') {
      sanitized[key] = sanitizers[key](value);
    } else if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    }
  });

  return sanitized;
};

